import { useState, type ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ImportaExcel from '@/components/data/DataImport';
import FormModal from '@/components/ui/FormModal';
import ProfileAvatar from '@/components/ui/ProfileAvatar';
import { useImport } from '@/context/ImportContext';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
}

function NavButton({
  label,
  description,
  onClick,
  icon,
  accent = 'default',
}: {
  label: string;
  description?: string;
  onClick: () => void;
  icon: ReactNode;
  accent?: 'default' | 'primary' | 'danger';
}) {
  const accents = {
    default:
      'border-slate-200/80 bg-white hover:border-secondary/30 hover:bg-slate-50',
    primary:
      'border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10',
    danger:
      'border-red-200/80 bg-red-50/50 hover:border-red-300 hover:bg-red-50',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200 hover:shadow-sm ${accents[accent]}`}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary group-hover:bg-secondary/15">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-secondary">{label}</span>
        {description ? (
          <span className="mt-0.5 block truncate text-xs text-gray-500">{description}</span>
        ) : null}
      </span>
      <svg
        className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-secondary/60"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

export default function AdminSidebar({ open, onClose }: AdminSidebarProps) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { downloadBackup, exportLoading } = useImport();
  const [modalType, setModalType] = useState<'import' | null>(null);

  const handleExport = async () => {
    try {
      await downloadBackup();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Errore durante l'export";
      window.alert(message);
    }
  };

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-[#1e2d4a]/40 backdrop-blur-[2px] transition-opacity duration-300 ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden={!open}
      />

      <aside
        className={`fixed right-0 top-0 z-50 flex h-full w-[min(100vw,22rem)] flex-col bg-slate-50 shadow-2xl transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#2e436a] via-[#3d5280] to-[#1e2d4a] px-5 pb-6 pt-5 text-white">
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#cd212a]/25 blur-2xl" />
          <div className="relative flex items-start justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
                Menu
              </p>
              <h2 className="mt-1 text-lg font-bold tracking-tight">Impostazioni</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Chiudi menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Profilo compatto nell'header */}
          <div className="relative mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm">
            <ProfileAvatar
              name={user?.username || 'U'}
              src={user?.profile_picture_url}
              size="md"
              className="ring-2 ring-white"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{user?.username}</p>
              {user?.email ? (
                <p className="truncate text-xs text-white/65">{user.email}</p>
              ) : null}
            </div>
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                user?.role === 'admin'
                  ? 'bg-primary/90 text-white'
                  : 'bg-white/20 text-white/90'
              }`}
            >
              {user?.role || 'user'}
            </span>
          </div>
        </div>

        {/* Contenuto scrollabile */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 pb-28">
          {isAdmin && (
            <section>
              <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-secondary/45">
                Amministrazione
              </p>
              <div className="space-y-2">
                <NavButton
                  label="Utenti"
                  description="Gestione account e ruoli"
                  onClick={() => go('/admin/users')}
                  icon={
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  }
                />
                <NavButton
                  label="Impostazioni generali"
                  description="Associati, servizi, esiti, database"
                  onClick={() => go('/settings')}
                  icon={
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  }
                />
              </div>
            </section>
          )}

          {isAdmin && (
            <section>
              <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-secondary/45">
                Dati
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setModalType('import')}
                  className="flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3 py-4 text-center transition hover:border-secondary/30 hover:bg-slate-50 hover:shadow-sm"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-700">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </span>
                  <span className="text-sm font-semibold text-secondary">Importa</span>
                </button>
                <button
                  type="button"
                  disabled={exportLoading}
                  onClick={handleExport}
                  className="flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3 py-4 text-center transition hover:border-secondary/30 hover:bg-slate-50 hover:shadow-sm disabled:cursor-wait disabled:opacity-60"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10 text-sky-700">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </span>
                  <span className="text-sm font-semibold text-secondary">
                    {exportLoading ? 'Export…' : 'Esporta'}
                  </span>
                </button>
              </div>
            </section>
          )}
        </div>

        {/* Footer logout */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-200/80 bg-white/95 p-4 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => {
              void logout().then(() => navigate('/'));
            }}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 py-3 text-sm font-semibold text-primary transition hover:border-red-300 hover:bg-red-100"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Esci dall'account
          </button>
        </div>
      </aside>

      {modalType === 'import' && isAdmin && (
        <FormModal
          eyebrow="Dati"
          title="Importa Excel"
          description="Carica un file .xlsx per importare lead e contatti nel database."
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          }
          onClose={() => setModalType(null)}
        >
          <ImportaExcel />
        </FormModal>
      )}
    </>
  );
}
